import { useForm } from 'react-hook-form';
import styles from './TestsPage.module.css';
import Utils from '../../../Utils';
import Line from '../../../Components/Charts/Line/Line';
import { ProgressBar } from 'react-bootstrap';
import { Link, useParams } from 'react-router';
import { useUser } from '../../../Context/useUser';
import { useVisitingUser } from '../../../Hooks/useVisitingUser';
import API from '../../../Utils/api';
import { toast } from 'react-toastify';
import { useState } from 'react';
import { useEffect } from 'react';
import Table from '../../../Components/Table/Table';

export default function T30v2TestPage () {
    const { user } = useUser();

    const { athleteId } = useParams();

    const visitedUser = useVisitingUser(athleteId);

    const currentUser = athleteId ? visitedUser : user;

    const [t30Tests, setT30Tests] = useState([]);
    const [testsLoading, setTestsLoading] = useState(true);

    const [selectedRows, setSelectedRows] = useState(t30Tests);

    const handleRowClick = row => {
        setSelectedRows(prev => {
            const alreadySelected = prev.some(r => r._id === row._id);
            if (alreadySelected) {
                return prev.filter(r => r._id !== row._id);
            } else {
                return [...prev, row];
            }
        });
    };

    useEffect(() => {
        fetchAndSetData(currentUser);
    }, [currentUser]);

    const fetchAndSetData = async currentUser => {
        if (!currentUser) return;

        try {
            const { data } = await API.getTest30TestsByUserId(currentUser._id);
            if (data) {
                setT30Tests(data);
                setSelectedRows(data[0] ? [data[0]] : []);
            }
        } catch (error) {
            toast.error(`Failed to fetch tests - please contact support`, {
                position: 'bottom-center'
            });
        } finally {
            setTestsLoading(false);
        }
    };

    const {
        register,
        handleSubmit,
        watch,
        reset,
        formState: { errors }
    } = useForm({
        defaultValues: {
            meters50: 50,
            time30: '30:00,00',
            time50: '00:00,00',
            distance: 0,
            poolSize: '',
            testDate: ''
        }
    });

    const currentInput = watch();

    const onSubmit = async () => {
        try {
            const testDateValue = currentInput.testDate
                ? new Date(currentInput.testDate).getTime()
                : undefined;

            const { data } = await API.createTest30Test(currentUser._id, {
                time50Meters: time50,
                distance30Minutes: distance30,
                poolSize: currentInput.poolSize ? Number(currentInput.poolSize) : null,
                testDate: testDateValue
            });
            currentUser.currentCSS = Number(data.css);
            currentUser.currentMSS = Number(data.mss);
            toast.success('Test created successfully!', {
                position: 'bottom-center'
            });
            await fetchAndSetData(currentUser);
        } catch (error) {
            toast.error(`Failed to create test - please contact support`, {
                position: 'bottom-center'
            });
        }
        reset();
    };

    function parseDuration (durationStr) {
        // Expects "mm:ss,ms"
        const match = durationStr.match(/^([0-5]?\d):([0-5]\d),(\d{1,3})$/);
        if (!match) return 0;
        const min = Number(match[1]);
        const sec = Number(match[2]);
        const ms = Number(match[3].padEnd(3, '0')); // pad to milliseconds if needed
        return min * 60 + sec + ms / 1000;
    }

    function calculateVelocity (distance, durationStr) {
        // durationStr is in "mm:ss" format
        if (!distance || !durationStr) return 0;
        const totalSeconds = parseDuration(durationStr);
        if (totalSeconds === 0) return 0;
        return (distance / totalSeconds).toFixed(2);
    }

    const time50 = currentInput.time50 ? parseDuration(currentInput.time50) : 0;
    const distance30 = Number(currentInput.distance) || 0;

    const velocity50 = time50 ? 50 / time50 : 0;
    const velocity30 = currentInput.distance ? currentInput.distance / 1800 : 0;

    const calculateCSS = distance30Minutes => (distance30Minutes ? distance30Minutes / 1800 : 0);
    const calculateMSS = time50Meters => (time50Meters ? 50 / time50Meters : 0);

    const columns = [
        {
            Header: 'Date',
            accessor: 'testDate',
            Cell: ({ row }) => {
                const date = new Date(row.original?.testDate);
                return (
                    <div>
                        {date.toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false
                        })}
                    </div>
                );
            }
        },
        {
            id: 'time50m',
            Header: () => <div className={styles.center}>Time 50m (sec)</div>,
            accessor: 'time50m',
            Cell: ({ row }) => <div className={styles.center}>{row.original.time50Meters}</div>
        },
        {
            id: 'distance30m',
            Header: () => <div className={styles.center}>Distance 30 minutes (m)</div>,
            accessor: 'distance30m',
            Cell: ({ row }) => <div className={styles.center}>{row.original.distance30Minutes}</div>
        },
        {
            id: 'poolSize',
            Header: () => <div className={styles.center}>Pool Size</div>,
            accessor: 'pool',
            Cell: ({ row }) => (
                <div className={styles.center}>{row.original.poolSize ? row.original.poolSize + 'm' : 'N/A'}</div>
            )
        },
        {
            id: 'css',
            Header: () => <div className={styles.center}>CSS</div>,
            accessor: 'css',
            Cell: ({ row }) => <div className={styles.center}>{row.original.css?.toFixed(2)}</div>
        },
        {
            id: 'mss',
            Header: () => <div className={styles.center}>MSS</div>,
            accessor: 'mss',
            Cell: ({ row }) => <div className={styles.center}>{row.original.mss?.toFixed(2)}</div>
        }
    ];

    const currentTestData = {
        time50Meters: time50,
        distance30Minutes: distance30,
        velocity50Meters: velocity50,
        velocity30Minutes: velocity30
    };

    const combinedChartData = [
        ...selectedRows,
        // Only include current input if both times are valid (to prevent plotting 0,0)
        ...(time50 > 0 && distance30 > 0 ? [currentTestData] : [])
    ];

    const datasetsDistanceTime = combinedChartData.map(row => {
        // Generate a unique color for each line (each test date)
        const { red, green, blue } = Utils.randomColorGenerator();
        return {
            label: row.testDate
                ? `Test on ${new Date(row.testDate).toLocaleDateString('en-GB')}`
                : 'Current Test',
            data: [
                { x: row.time50Meters, y: 50 },
                { x: 1800, y: row.distance30Minutes }
            ],
            borderColor: `rgb(${red}, ${green}, ${blue})`,
            backgroundColor: `rgba(${red}, ${green}, ${blue}, 0.5)`,
            tension: 0.1,
            fill: false,
            showLine: true
        };
    });

    const datasetsDistanceVelocity = combinedChartData
        .filter(row => row.time50Meters > 0 && row.distance30Minutes > 0)
        .map(row => {
            const { red, green, blue } = Utils.randomColorGenerator();
            return {
                label: row.testDate
                    ? `Test on ${new Date(row.testDate).toLocaleDateString('en-GB')}`
                    : 'Current Test',
                data: [
                    { x: row.velocity50Meters, y: 50 },
                    { x: row.velocity30Minutes, y: row.distance30Minutes }
                ],
                borderColor: `rgb(${red}, ${green}, ${blue})`,
                backgroundColor: `rgba(${red}, ${green}, ${blue}, 0.5)`,
                tension: 0.1,
                fill: false,
                showLine: true
            };
        });

    return (
        <div className={styles.testPage} data-tests-page>
            <header className={styles.pageHead}>
                <div className={styles.pageHeadText}>
                    <h2 className={styles.pageTitle}>T30 & 50M test</h2>
                    <p className={styles.pageSub}>A 50 m time trial and a 30 minute swim · intermediate or advanced swimmers · one or two days</p>
                </div>
                <div className={styles.difficulty}>
                    <span className={styles.difficultyLabel} aria-hidden='true'>
                        Difficulty
                    </span>
                    <ProgressBar className={styles.difficultyBar}>
                        <ProgressBar variant='warning' now={50} aria-label='Test difficulty' />
                    </ProgressBar>
                </div>
            </header>
            <div className={styles.entryGrid}>
                <section className={`${styles.panel} ${styles.areaForm}`} aria-labelledby='t30-data-title'>
                    <div className={styles.panelHead}>
                        <div className={styles.panelHeadText}>
                            <h3 id='t30-data-title' className={styles.panelTitle}>
                                Test data
                            </h3>
                            <p className={styles.panelSub}>
                                Enter your times as mm:ss,ms. Velocity is calculated as you type.
                            </p>
                        </div>
                    </div>
                    <form className={`${styles.panelBody} ${styles.entryForm}`}>
                    <fieldset className={styles.fieldGroup}>
                        <legend className={styles.fieldGroupLegend}>50 m trial</legend>
                        <div className={styles.field}>
                            <label htmlFor='t30-meters50' className={styles.fieldLabel}>
                                Distance (m)
                            </label>
                            <input
                                id='t30-meters50'
                                disabled={true}
                                {...register('meters50', { required: true })}
                                className={`${styles.fieldInput} form-control`}
                            />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor='t30-time50' className={styles.fieldLabel}>
                                Time (mm:ss,ms)
                            </label>
                            <input
                                id='t30-time50'
                                type='text'
                                placeholder='mm:ss,ms'
                                {...register('time50', {
                                    required: 'Time is required',
                                    pattern: {
                                        value: /^([0-5]?\d):([0-5]\d),(\d{1,3})$/,
                                        message: "'mm:ss,ms' format is required"
                                    }
                                })}
                                aria-invalid={errors.time50 ? true : undefined}
                                aria-describedby={errors.time50 ? 't30-time50-error' : undefined}
                                className={`${styles.fieldInput} form-control`}
                            />
                            {errors.time50 && (
                                <span id='t30-time50-error' className={styles.fieldError}>
                                    {errors.time50.message}
                                </span>
                            )}
                        </div>
                        <div className={styles.field}>
                            <label htmlFor='t30-velocity50' className={styles.fieldLabel}>
                                Velocity (m/sec)
                            </label>
                            <input
                                id='t30-velocity50'
                                disabled={true}
                                type='number'
                                value={calculateVelocity(
                                    currentInput.meters50,
                                    currentInput.time50
                                )}
                                className={`${styles.fieldInput} form-control`}
                            />
                        </div>
                    </fieldset>
                    <fieldset className={styles.fieldGroup}>
                        <legend className={styles.fieldGroupLegend}>30 minute swim (T30)</legend>
                        <div className={styles.field}>
                            <label htmlFor='t30-distance' className={styles.fieldLabel}>
                                Distance (m)
                            </label>
                            <input
                                id='t30-distance'
                                type='number'
                                {...register('distance', {
                                    required: 'Distance is required',
                                    valueAsNumber: true,
                                    validate: value =>
                                        value !== undefined && !isNaN(value) && value > 0
                                            ? true
                                            : 'Only positive numbers are allowed for distance'
                                })}
                                aria-invalid={errors.distance ? true : undefined}
                                aria-describedby={errors.distance ? 't30-distance-error' : undefined}
                                className={`${styles.fieldInput} form-control`}
                            />
                            {errors.distance && (
                                <span id='t30-distance-error' className={styles.fieldError}>
                                    {errors.distance.message}
                                </span>
                            )}
                        </div>
                        <div className={styles.field}>
                            <label htmlFor='t30-time30' className={styles.fieldLabel}>
                                Time (mm:ss,ms)
                            </label>
                            <input
                                id='t30-time30'
                                disabled={true}
                                placeholder='mm:ss,ms'
                                type='text'
                                {...register('time30', {
                                    required: true,
                                    pattern: {
                                        value: /^([0-5]?\d):([0-5]\d),(\d{1,3})$/,
                                        message: "'mm:ss,ms' format is required"
                                    }
                                })}
                                className={`${styles.fieldInput} form-control`}
                            />
                        </div>
                        <div className={styles.field}>
                            <label htmlFor='t30-velocity30' className={styles.fieldLabel}>
                                Velocity (m/sec)
                            </label>
                            <input
                                id='t30-velocity30'
                                disabled={true}
                                type='number'
                                value={calculateVelocity(
                                    currentInput.distance,
                                    currentInput.time30
                                )}
                                className={`${styles.fieldInput} form-control`}
                            />
                        </div>
                    </fieldset>
                    <fieldset className={`${styles.fieldGroup} ${styles.detailsGroup}`}>
                        <legend className={styles.fieldGroupLegend}>Session details</legend>
                        <div className={styles.field}>
                            <label htmlFor='t30-poolSize' className={styles.fieldLabel}>
                                Pool size (m) <span className={styles.fieldHint}>· for reference only</span>
                            </label>
                            <select
                                id='t30-poolSize'
                                className={`${styles.fieldInput} form-control`}
                                {...register('poolSize')}
                            >
                                <option value={''}>-</option>
                                <option value={25}>25m</option>
                                <option value={50}>50m</option>
                            </select>
                        </div>
                        <div className={styles.field}>
                            <label htmlFor='t30-testDate' className={styles.fieldLabel}>
                                Test date & time <span className={styles.fieldHint}>· defaults to now</span>
                            </label>
                            <input
                                id='t30-testDate'
                                type='datetime-local'
                                {...register('testDate')}
                                className={`${styles.fieldInput} form-control`}
                            />
                        </div>
                    </fieldset>
                    <div className={styles.formActions}>
                        <button
                            className={`${styles.submitButton} btn btn-primary`}
                            onClick={handleSubmit(onSubmit)}
                        >
                            Submit
                        </button>
                    </div>
                    </form>
                </section>
                <section className={`${styles.panel} ${styles.areaSummary}`} aria-labelledby='t30-summary-title'>
                    <div className={styles.panelHead}>
                        <div className={styles.panelHeadText}>
                            <h3 id='t30-summary-title' className={styles.panelTitle}>
                                Summary
                            </h3>
                            <p className={styles.panelSub}>This entry next to the latest saved values.</p>
                        </div>
                    </div>
                    <table className={styles.summaryTable}>
                        <thead>
                            <tr>
                                <th scope='col' className={styles.metricName}>
                                    Metric
                                </th>
                                <th scope='col'>Current test</th>
                                <th scope='col'>
                                    Latest for {currentUser ? currentUser.firstName : ''}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <th scope='row' className={styles.metricName}>
                                    <span aria-hidden='true' className={`${styles.metricMarker} ${styles.markerCss}`} />
                                    Critical Swim Speed (CSS)
                                </th>
                                <td className={styles.metricValue}>
                                    {calculateCSS(distance30).toFixed(3)}
                                </td>
                                <td className={styles.metricValue}>{currentUser?.currentCSS?.toFixed(3) || 0}</td>
                            </tr>
                            <tr>
                                <th scope='row' className={styles.metricName}>
                                    <span aria-hidden='true' className={`${styles.metricMarker} ${styles.markerMss}`} />
                                    Maximum Swim Speed (MSS)
                                </th>
                                <td className={styles.metricValue}>{calculateMSS(time50).toFixed(3) || 0}</td>
                                <td className={styles.metricValue}>{currentUser?.currentMSS?.toFixed(3) || 0}</td>
                            </tr>
                        </tbody>
                    </table>
                    <p className={styles.summaryUnit}>Velocities in m/sec.</p>
                </section>
                <section
                    className={`${styles.panel} ${styles.areaHistory}`}
                    aria-labelledby='t30-history-title'
                    data-tests-history
                >
                    <div className={styles.panelHead}>
                        <div className={styles.panelHeadText}>
                            <h3 id='t30-history-title' className={styles.panelTitle}>
                                Previous tests
                            </h3>
                            <p className={styles.panelSub}>
                                Select a row to show or hide it on the charts below. Your current entry is added once its
                                times are valid.
                            </p>
                        </div>
                    </div>
                    <div className={styles.panelFlush}>
                        <Table
                            onRowClick={handleRowClick}
                            columns={columns}
                            data={t30Tests}
                            isLoading={testsLoading}
                            rowsPerPage={7}
                            updatePageSize={false}
                            pageSkipping={false}
                            selected={selectedRows}
                            showSelectionIndicator={true}
                            tableLayout='auto'
                        />
                    </div>
                </section>
                <section className={`${styles.panel} ${styles.areaGuide}`} aria-labelledby='t30-guide-title'>
                    <div className={styles.panelHead}>
                        <h3 id='t30-guide-title' className={styles.panelTitle}>
                            How to run this test
                        </h3>
                    </div>
                    <div className={`${styles.panelBody} ${styles.guide}`}>
                    <p>
                        This test is aimed for <b>advanced</b> or <b>intermediate</b> swimmers.
                        It takes <b>one</b> or <b>two</b> <b>days</b> to complete this test.
                    </p>
                    <p>
                        If you complete the test in <b>one</b> day, you must complete the{' '}
                        <b>50 m</b> test first and then, after at <b>least half an hour</b>, the
                        T30 test (30 minutes of swimming at the best possible pace).
                    </p>
                    <p>
                        If we complete the test in <b>two days</b>, we should complete one test
                        in one day and the second test in the next day.
                    </p>
                    <p>The two trials will start in the pool without a start.</p>
                    </div>
                </section>
            </div>
            <div className={styles.chartGrid}>
                <section className={styles.panel} aria-labelledby='t30-chart-time-title'>
                    <h3 id='t30-chart-time-title' className={styles.srOnly}>
                        Distance - Time
                    </h3>
                    <div className={styles.chartBody}>
                        <Line
                            title={'Distance - Time'}
                            titleY={'Distance (m)'}
                            titleX={'Time (sec)'}
                            datasets={datasetsDistanceTime}
                            xAxisType='linear'
                        />
                    </div>
                </section>
                <section className={styles.panel} aria-labelledby='t30-chart-velocity-title'>
                    <h3 id='t30-chart-velocity-title' className={styles.srOnly}>
                        Distance - Velocity
                    </h3>
                    <div className={styles.chartBody}>
                        <Line
                            title={'Distance - Velocity'}
                            titleY={'Distance (m)'}
                            titleX={'Velocity (m/sec)'}
                            datasets={datasetsDistanceVelocity}
                            xAxisType='linear'
                        />
                    </div>
                </section>
            </div>
            <p className={styles.footNote}>
                For your Training Zones Limits check your{' '}
                <Link to='../../dashboard' className={styles.footLink}>
                    Dashboard
                </Link>
                .
            </p>
        </div>
    );
}
